import React from 'react';
import {createGlobalStyle} from 'styled-components';
import {Transition, TransitionProps} from '../Transition';
import {getTimeouts} from '../utils';

export type SlideProps = {
  direction: 'up' | 'down' | 'left' | 'right';
  transformLength?: number;
  onUnmount?: () => void;
} & Pick<TransitionProps, 'mounted' | 'duration' | 'delay'>;

export const Slide: React.FC<React.PropsWithChildren<SlideProps>> = ({
  children,
  mounted,
  duration,
  delay,
  direction,
  transformLength = 100,
  onUnmount,
}) => {
  const nodeRef = React.useRef(null);

  const timeouts = getTimeouts({duration, delay});
  const enterAnimation = {
    up: mounted ? 'slideUpEnter' : 'slideUpExit',
    down: mounted ? 'slideDownEnter' : 'slideDownExit',
    left: mounted ? 'slideLeftEnter' : 'slideLeftExit',
    right: mounted ? 'slideRightEnter' : 'slideRightExit',
  }[direction];
  const animationDuration = mounted
    ? timeouts.mountDuration
    : timeouts.unmountDuration;
  const animationDelay = mounted ? timeouts.mountDelay : timeouts.unmountDelay;

  const styles = {
    willChange: 'transform, opacity',
    animation: `${enterAnimation} ${animationDuration}ms cubic-bezier(0.75, 0.23, 0.57, 0.65) ${animationDelay}ms forwards`,
  };

  return (
    <Transition
      nodeRef={nodeRef}
      mounted={mounted}
      duration={duration}
      delay={delay}
      onUnmount={onUnmount}
    >
      {React.Children.map(children, (child: any) => {
        return React.cloneElement(child, {
          style: {
            ...(child.props?.style ? child.props.style : {}),
            ...styles,
          },
          ref: nodeRef,
        });
      })}
      <Animations transformLength={transformLength} />
    </Transition>
  );
};

const Animations = createGlobalStyle<Pick<SlideProps, 'transformLength'>>`
  @keyframes slideUpEnter {
    from { transform: translateY(${({transformLength}) =>
      transformLength}%); opacity: 0; }
    to { transform: translateY(0%); opacity: 1; }
  }
  @keyframes slideUpExit {
    from { transform: translateY(0%); opacity: 1; }
    to { transform: translateY(${({transformLength}) =>
      transformLength}%); opacity: 0; }
  }
  @keyframes slideDownEnter {
    from { transform: translateY(-${({transformLength}) =>
      transformLength}%); opacity: 0; }
    to { transform: translateY(0%); opacity: 1; }
  }
  @keyframes slideDownExit {
    from { transform: translateY(0%); opacity: 1; }
    to { transform: translateY(-${({transformLength}) =>
      transformLength}%); opacity: 0; }
  }
  @keyframes slideRightEnter {
    from { transform: translateX(-${({transformLength}) =>
      transformLength}%); opacity: 0; }
    to { transform: translateX(0%); opacity: 1; }
  }
  @keyframes slideRightExit {
    from { transform: translateX(0%); opacity: 1; }
    to { transform: translateX(-${({transformLength}) =>
      transformLength}%); opacity: 0; }
  }
  @keyframes slideLeftEnter {
    from { transform: translateX(${({transformLength}) =>
      transformLength}%); opacity: 0; }
    to { transform: translateX(0%); opacity: 1; }
  }
  @keyframes slideLeftExit {
    from { transform: translateX(0%); opacity: 1; }
    to { transform: translateX(${({transformLength}) =>
      transformLength}%); opacity: 0; }
  }
`;
